import React, { useState } from 'react';
import { useClient } from '../contexts/ClientContext';
import { ALL_MEDIUMS } from '../data/mediums';
import { Medium } from '../types';
import { getApiUrl, formatImageUrl, compressImage } from '../services/socket';
import { SparklesIcon, XIcon, LogOutIcon, CameraIcon, GiftIcon, MessageCircleIcon, StarIcon, HistoryIcon, CreditCardIcon, SettingsIcon } from './Icons';

interface ClientProfilePageProps {
    onNavigate: (page: string) => void;
    onChatStart?: (medium: Medium) => void;
    onOpenSarah?: () => void;
    onOpenPremium?: () => void;
    onClose?: () => void;
}

export const ClientProfilePage: React.FC<ClientProfilePageProps> = ({
    onNavigate,
    onChatStart,
    onOpenSarah,
    onOpenPremium,
    onClose
}) => {
    const { client, logoutClient, openAuthModal, claimDailyBonus } = useClient();
    const [activeSection, setActiveSection] = useState<'consultations' | 'history' | 'favorites' | 'plans' | 'settings'>('consultations');
    const [bonusMessage, setBonusMessage] = useState<string | null>(null);

    const handleCloseOrHome = () => {
        if (onClose) onClose();
        onNavigate('home');
    };

    const handleLogout = () => {
        logoutClient();
        if (onClose) onClose();
        onNavigate('home');
    };

    const handleClaimDailyBonus = () => {
        if (!claimDailyBonus) return;
        const res = claimDailyBonus();
        setBonusMessage(res.message);
        setTimeout(() => setBonusMessage(null), 5000);
    };

    const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !client) return;
        try {
            const base64 = await compressImage(file, 600, 600, 0.85);
            let avatarUrl = base64;
            try {
                const res = await fetch(`${getApiUrl()}/api/upload`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ image: base64, name: `avatar_${client.id}` })
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.url) {
                        avatarUrl = formatImageUrl(data.url);
                    }
                }
            } catch (err) {
                console.warn("Upload fallback base64 used:", err);
            }
            updateClientProfile({ avatar: avatarUrl });
            if (typeof window !== 'undefined') {
                localStorage.setItem(`astro_client_avatar_${client.id}`, avatarUrl);
            }
        } catch (err) {
            console.error("Erreur compression avatar:", err);
        }
    };

    if (!client) {
        return (
            <div className="min-h-screen bg-[#08081A] text-white flex flex-col items-center justify-center p-6 text-center animate-fade-in">
                <div className="w-20 h-20 rounded-full bg-purple-950/60 border border-purple-500/40 flex items-center justify-center text-4xl mb-4 shadow-xl animate-float">
                    
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif font-black text-white mb-2">
                    Espace Client Privé Astrox.fr
                </h2>
                <p className="text-xs text-purple-300/80 max-w-sm leading-relaxed mb-6">
                    Connectez-vous pour retrouver vos consultations, votre solde d'Astres, vos voyants favoris et vos avantages fidélité.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={() => openAuthModal('login')}
                        className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black text-xs px-8 py-3.5 rounded-full shadow-lg transition-all active:scale-95 cursor-pointer border border-purple-400/40"
                    >
                        Se connecter / Créer mon compte
                    </button>
                    <button
                        onClick={handleCloseOrHome}
                        className="bg-[#12102A] hover:bg-[#1A1033] text-purple-200 font-bold text-xs px-6 py-3.5 rounded-full border border-purple-800/60 transition-all cursor-pointer"
                    >
                        Retour à l'accueil
                    </button>
                </div>
            </div>
        );
    }

    const todayStr = new Date().toLocaleDateString('en-CA');
    const hasClaimedToday = client.claimedDailyBonusDate === todayStr || (typeof window !== 'undefined' && localStorage.getItem(`astro_daily_claimed_${client.id}_${todayStr}`) === 'true');
    const favoriteMediums = ALL_MEDIUMS.filter(m => (client.favorites || []).includes(m.id));

    // Calculs réels et fiables du portefeuille d'Astres
    const totalAstres = typeof client.loyaltyPoints === 'number' ? client.loyaltyPoints : (client.paidAstres || 0) + (client.freeAstres || 0);
    const paidAstres = typeof client.paidAstres === 'number' ? client.paidAstres : 0;
    const freeAstres = typeof client.freeAstres === 'number' ? client.freeAstres : 0;

    return (
        <div className="min-h-screen bg-[#08081A] text-white pt-6 pb-28 px-4 max-w-5xl mx-auto animate-fade-in">
            
            {/* Top Bar: Bouton Fermer / Retour Accueil */}
            <div className="flex items-center justify-between gap-2 sm:gap-3 mb-6 pb-3 border-b border-purple-900/40">
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleCloseOrHome}
                        className="flex items-center gap-1.5 text-xs font-bold text-purple-200 hover:text-white bg-[#12102A] hover:bg-[#1A1033] border border-purple-800/60 px-3.5 py-2 rounded-full transition-all cursor-pointer shadow-md active:scale-95"
                    >
                        <span>←</span>
                        <span className="hidden xs:inline">Accueil</span>
                    </button>

                    <button
                        onClick={handleCloseOrHome}
                        className="flex items-center gap-1 text-xs font-bold text-amber-300 hover:text-white bg-purple-950/70 hover:bg-purple-900 border border-gold-500/40 px-3.5 py-2 rounded-full transition-all cursor-pointer shadow-md active:scale-95"
                    >
                        <XIcon className="w-4 h-4" />
                        <span>Fermer</span>
                    </button>
                </div>

                <button
                    onClick={handleLogout}
                    className="flex items-center gap-1.5 text-xs font-bold text-red-300 hover:text-white bg-red-950/40 hover:bg-red-900/60 border border-red-500/40 px-3.5 py-2 rounded-full transition-all cursor-pointer active:scale-95 shadow-md"
                >
                    <LogOutIcon className="w-4 h-4 text-rose-400" />
                    <span className="hidden xs:inline">Se Déconnecter</span>
                </button>
            </div>

            {/* Header Profil Client */}
            <div className="p-6 rounded-3xl bg-gradient-to-r from-[#1A1033] via-[#12102A] to-[#1A1033] border border-purple-900/50 shadow-2xl mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                    <div className="relative group shrink-0">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-tr from-gold-500 via-amber-400 to-yellow-600 p-0.5 shadow-xl overflow-hidden">
                            {client.avatar ? (
                                <img src={client.avatar} alt={client.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                                <div className="w-full h-full rounded-full bg-black/80 flex items-center justify-center text-amber-300 font-serif font-bold text-2xl">
                                    {client.name.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <label className="absolute bottom-0 right-0 p-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black shadow-lg cursor-pointer transition-transform hover:scale-110 active:scale-95 border-2 border-black" title="Changer ma photo de profil">
                            <CameraIcon className="w-3.5 h-3.5 text-white" />
                            <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleAvatarUpload}
                                className="hidden" 
                            />
                        </label>
                    </div>

                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl sm:text-2xl font-serif font-black text-white">
                                Bienvenue, {client.name}
                            </h1>
                            <span className="text-xs bg-gold-500/20 text-gold-300 border border-gold-500/40 px-2.5 py-0.5 rounded-full font-black">
                                {client.loyaltyTier || 'Bronze'}
                            </span>
                        </div>
                        <p className="text-xs text-purple-300/80 mt-1">
                            {client.email || client.phone || 'Membre Privé Astrox.fr'} • Inscrit(e) depuis {new Date(client.createdAt || Date.now()).toLocaleDateString('fr-FR')}
                        </p>
                    </div>
                </div>

                {/* Solde d'Astres & Action Recharge */}
                <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                    <div className="bg-black/60 border border-gold-400/40 px-4 py-2.5 rounded-2xl text-left">
                        <span className="text-[10px] text-purple-300 uppercase tracking-wider block">Solde Disponible</span>
                        <span className="text-lg font-black text-amber-300 font-mono flex items-center gap-1.5">
                            <SparklesIcon className="w-4 h-4 text-amber-400" />
                            {totalAstres} Astres
                        </span>
                    </div>

                    <button
                        onClick={onOpenSarah || onOpenPremium || (() => onNavigate('tarifs'))}
                        className="bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-400 hover:to-amber-400 text-black font-black text-xs px-4 py-3 rounded-2xl shadow-lg transition-all active:scale-95 cursor-pointer whitespace-nowrap"
                    >
                        + Recharger
                    </button>
                </div>
            </div>

            {/* --- CARTE DE RÉCLAMATION MANUELLE DU BONUS QUOTIDIEN --- */}
            <div className="mb-6 p-4 sm:p-5 rounded-3xl bg-gradient-to-r from-[#1E113E] via-[#140C2C] to-[#1E113E] border border-gold-500/40 shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-gold-500/20 border border-gold-400/50 flex items-center justify-center shrink-0 shadow-inner">
                        <GiftIcon className="w-6 h-6 text-gold-400" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-serif font-bold text-white">
                                Bonus Quotidien de Fidélité
                            </h3>
                            <span className="text-[10px] bg-amber-400 text-black font-black px-2 py-0.2 rounded-full uppercase">
                                +2 Astres / jour
                            </span>
                        </div>
                        <p className="text-xs text-purple-200/80 mt-0.5 font-light">
                            Cliquez ci-contre chaque jour pour récupérer vos 2 Astres de consultation offerts.
                        </p>
                    </div>
                </div>

                <div className="w-full sm:w-auto shrink-0">
                    <button
                        onClick={handleClaimDailyBonus}
                        disabled={hasClaimedToday}
                        className={`w-full sm:w-auto px-6 py-3 rounded-full text-xs font-black uppercase tracking-wider transition-all cursor-pointer shadow-lg flex items-center justify-center gap-2 ${
                            hasClaimedToday
                                ? 'bg-zinc-800/80 text-zinc-400 border border-zinc-700 cursor-not-allowed'
                                : 'bg-gradient-to-r from-gold-500 via-amber-400 to-gold-500 hover:from-gold-400 hover:to-amber-300 text-black shadow-[0_0_20px_rgba(251,191,36,0.5)] border border-yellow-200 active:scale-95'
                        }`}
                    >
                        <span>{hasClaimedToday ? 'Bonus du jour déjà récupéré' : 'Réclamer mon bonus (+2 Astres)'}</span>
                    </button>
                </div>
            </div>

            {bonusMessage && (
                <div className="mb-6 p-3 rounded-2xl bg-emerald-950/80 border border-emerald-500/40 text-emerald-200 text-xs text-center animate-fade-in">
                    {bonusMessage}
                </div>
            )}

            {/* Navigation Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 no-scrollbar">
                {[
                    { id: 'consultations', label: 'Consultations', badge: null },
                    { id: 'favorites', label: 'Mes Favoris', badge: favoriteMediums.length },
                    { id: 'history', label: 'Solde & Portefeuille', badge: null },
                    { id: 'plans', label: 'Tarifs & Forfaits', badge: null },
                    { id: 'settings', label: 'Mon Compte', badge: null }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveSection(tab.id as any)}
                        className={`px-4 py-2.5 rounded-2xl text-xs font-bold transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                            activeSection === tab.id
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-950/60 scale-105 border border-purple-400/50'
                                : 'bg-[#12102A]/80 hover:bg-[#1A1033] text-purple-200/80 hover:text-white border border-purple-900/40'
                        }`}
                    >
                        <span>{tab.label}</span>
                        {tab.badge !== null && tab.badge > 0 && (
                            <span className="text-[10px] bg-amber-400 text-black font-black px-1.5 py-0.2 rounded-full">
                                {tab.badge}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Contenu de la Section */}
            <div className="bg-[#12102A]/90 border border-purple-900/50 rounded-3xl p-6 shadow-2xl">
                
                {/* SECTION 1 : CONSULTATIONS & MESSAGERIE */}
                {activeSection === 'consultations' && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-serif font-bold text-white flex items-center gap-2">
                                <MessageCircleIcon className="w-4 h-4 text-purple-300 inline mr-1.5" /> Vos Échanges Récents
                            </h3>
                            <button
                                onClick={() => onNavigate('messages')}
                                className="text-xs text-amber-300 hover:text-amber-200 font-bold flex items-center gap-1"
                            >
                                <span>Ouvrir la messagerie</span>
                                <span>→</span>
                            </button>
                        </div>
                        <p className="text-xs text-purple-300/80 leading-relaxed">
                            Accédez à vos salons de consultation privés avec vos médiums et poursuivez vos discussions confidentielles en toute sérénité.
                        </p>
                        <button
                            onClick={() => onNavigate('messages')}
                            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-xs uppercase tracking-wider shadow-lg transition-all cursor-pointer"
                        >
                            Accéder à ma boîte de réception
                        </button>
                    </div>
                )}

                {/* SECTION 2 : FAVORIS */}
                {activeSection === 'favorites' && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-serif font-bold text-white mb-2 flex items-center gap-2">
                            <StarIcon className="w-4 h-4 text-amber-400 fill-amber-400 inline mr-1.5" /> Vos Médiums Préférés ({favoriteMediums.length})
                        </h3>
                        {favoriteMediums.length === 0 ? (
                            <div className="text-center py-12 text-purple-300/70 space-y-3">
                                <SparklesIcon className="w-8 h-8 text-amber-300 mx-auto" />
                                <p className="text-xs">Vous n'avez pas encore ajouté de médiums à vos favoris.</p>
                                <button
                                    onClick={() => onNavigate('mediums')}
                                    className="text-xs bg-purple-900/60 text-purple-200 px-4 py-2 rounded-xl hover:bg-purple-800"
                                >
                                    Découvrir nos experts
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {favoriteMediums.map(med => (
                                    <div key={med.id} className="p-4 rounded-2xl bg-black/40 border border-purple-900/40 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <img src={med.imageUrl} alt={med.name} className="w-12 h-12 rounded-full object-cover border border-gold-400/50" />
                                            <div>
                                                <h4 className="text-sm font-bold text-white">{med.name}</h4>
                                                <p className="text-xs text-purple-300/70">{med.specialties.slice(0, 2).join(' • ')}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                if (onChatStart) onChatStart(med);
                                                else onNavigate('messages');
                                            }}
                                            className="px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold"
                                        >
                                            Consulter
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* SECTION 3 : HISTORIQUE DU SOLDE & PORTEFEUILLE */}
                {activeSection === 'history' && (
                    <div className="space-y-4 text-xs">
                        <h3 className="text-lg font-serif font-bold text-white mb-2 flex items-center gap-2">
                            <HistoryIcon className="w-4 h-4 text-purple-300 inline mr-1.5" /> Portefeuille & Détail des Astres
                        </h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="p-4 rounded-2xl bg-black/50 border border-gold-500/40 text-center space-y-1">
                                <span className="text-purple-300 text-[11px] block">Solde Total</span>
                                <span className="text-2xl font-black text-amber-300 font-mono block">{totalAstres}</span>
                                <span className="text-[10px] text-gray-400">Astres disponibles</span>
                            </div>

                            <div className="p-4 rounded-2xl bg-black/50 border border-purple-800/40 text-center space-y-1">
                                <span className="text-purple-300 text-[11px] block">Astres Achetés</span>
                                <span className="text-2xl font-black text-white font-mono block">{paidAstres}</span>
                                <span className="text-[10px] text-emerald-400 font-bold">Valables à vie (Illimités)</span>
                            </div>

                            <div className="p-4 rounded-2xl bg-black/50 border border-purple-800/40 text-center space-y-1">
                                <span className="text-purple-300 text-[11px] block">Astres Offerts</span>
                                <span className="text-2xl font-black text-purple-200 font-mono block">{freeAstres}</span>
                                <span className="text-[10px] text-amber-300 font-bold">Valables 30 jours</span>
                            </div>
                        </div>

                        {/* Liste des transactions */}
                        <div className="pt-2">
                            <span className="text-xs font-bold text-purple-300 block mb-2">Dernières Opérations :</span>
                            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                {(client.loyaltyTransactions || []).length === 0 ? (
                                    <p className="text-gray-400 italic">Aucune transaction pour le moment.</p>
                                ) : (
                                    client.loyaltyTransactions?.map(tx => (
                                        <div key={tx.id} className="p-2.5 rounded-xl bg-black/30 border border-purple-900/30 flex justify-between items-center text-xs">
                                            <div>
                                                <span className="text-white font-medium block">{tx.description}</span>
                                                <span className="text-[10px] text-gray-400">{tx.date}</span>
                                            </div>
                                            <span className={`font-mono font-bold ${tx.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {tx.amount > 0 ? `+${tx.amount}` : tx.amount} Astres
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* SECTION 4 : TARIFS & ABONNEMENTS */}
                {activeSection === 'plans' && (
                    <div className="space-y-4">
                        <h3 className="text-lg font-serif font-bold text-white mb-2 flex items-center gap-2">
                            <CreditCardIcon className="w-4 h-4 text-purple-300 inline mr-1.5" /> Forfaits & Recharges Astrox
                        </h3>
                        <p className="text-xs text-purple-300/80 leading-relaxed">
                            Accédez aux recharges sécurisées par Transcash et profitez de bonus offerts permanents crédités à vie sur votre compte.
                        </p>
                        <button
                            onClick={() => onNavigate('tarifs')}
                            className="w-full py-3 bg-gradient-to-r from-gold-500 via-amber-400 to-gold-500 text-black font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg cursor-pointer"
                        >
                            Voir la grille tarifaire complète
                        </button>
                    </div>
                )}

                {/* SECTION 5 : PARAMÈTRES & DÉCONNEXION */}
                {activeSection === 'settings' && (
                    <div className="space-y-4 text-xs">
                        <h3 className="text-lg font-serif font-bold text-white mb-2 flex items-center gap-2">
                            <SettingsIcon className="w-4 h-4 text-purple-300 inline mr-1.5" /> Informations du Compte
                        </h3>
                        <div className="space-y-3 bg-black/40 p-4 rounded-2xl border border-purple-900/40">
                            <div>
                                <span className="text-purple-400 font-bold block mb-1">Nom d'affichage :</span>
                                <span className="text-white font-medium">{client.name}</span>
                            </div>
                            <div>
                                <span className="text-purple-400 font-bold block mb-1">Téléphone :</span>
                                <span className="text-white font-mono">{client.phone || 'Non renseigné'}</span>
                            </div>
                            <div>
                                <span className="text-purple-400 font-bold block mb-1">Email :</span>
                                <span className="text-white font-mono">{client.email || 'Non renseigné'}</span>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default ClientProfilePage;
